/**
 * 排课 API（class-scheduling change 5-4，ADM-19/20/21 + 班级详情排课本签）。
 *
 * 对齐后端 scheduling BC 的 REST 契约：
 * - POST /schedules -> PublishSchedulesResult（发布 + 冲突预检；硬冲突 409 携带 error.details.conflicts）
 * - GET /schedules?classId= -> { items: ScheduleListItem[] }（规则列表 + 未来课次计数）
 * - DELETE /schedules/:id {confirm} -> EndScheduleResult（结束排课）
 * - GET /lesson-sessions?from=&to= -> { items: LessonSessionListItem[] }（周历，
 *   可选 studentId 过滤含 ENDED 历史班级课次，student-detail-cross-domain-tabs）
 * - GET /lesson-sessions/:id -> LessonSessionDetail（详情 + 学生摘要）
 * - POST /lesson-sessions/:id/adjustment -> LessonSessionDetail（D-09 调整）
 *
 * 路径从 api.config.ts 导入，禁止内联；Transport 统一解包 `{success, data}` 信封。
 */
import { ApiPaths } from './api.config';
import { http } from './transport';

/** 课次状态。 */
export type LessonSessionStatus = 'SCHEDULED' | 'CANCELLED' | 'COMPLETED';

/** 排课规则状态。 */
export type ScheduleStatus = 'ACTIVE' | 'ENDED';

/** 硬冲突类型（ADM-20 冲突明细）。 */
export type ScheduleConflictType = 'CLASS' | 'CLASSROOM' | 'TEACHER' | 'CAPACITY';

/** 软提醒类型。 */
export type SoftWarningType = 'OUT_OF_AVAILABILITY' | 'AVAILABILITY_EXCEPTION' | 'CROSS_CAMPUS';

/** 排课时间项（对话框录入）。 */
export interface ScheduleSlotInput {
  dayOfWeek: number;
  startTime: string;
  durationMinutes?: number;
}

/** 发布结果（SC-01）。 */
export interface PublishSchedulesResult {
  schedules: Array<{
    id: string;
    dayOfWeek: number;
    startTime: string;
    durationMinutes: number;
    teacherId: string;
    classroomId: string;
    status: ScheduleStatus;
    effectiveFrom: string;
    effectiveTo: string;
  }>;
  sessionCount: number;
  warnings: Array<{ type: SoftWarningType; dayOfWeek: number; message: string }>;
}

/** 规则列表项（SC-33：含未来课次派生计数）。 */
export interface ScheduleListItem {
  id: string;
  dayOfWeek: number;
  startTime: string;
  durationMinutes: number;
  classroomId: string;
  classroomName: string;
  teacherId: string;
  teacherName: string;
  status: ScheduleStatus;
  effectiveFrom: string;
  effectiveTo: string;
  futureScheduledSessionCount: number;
}

/** 结束排课结果。 */
export interface EndScheduleResult {
  id: string;
  status: ScheduleStatus;
  cancelledSessionCount: number;
}

/** 周历课次项（SC-37）。 */
export interface LessonSessionListItem {
  id: string;
  classId: string;
  className: string;
  classroomId: string;
  classroomName: string;
  sessionDate: string;
  startAt: string;
  endAt: string;
  status: LessonSessionStatus;
  classroomChanged: boolean | null;
}

/** 课次学生摘要项（SC-39：无可用账户 balance=null 显示「-」）。 */
export interface SessionStudentSummary {
  studentId: string;
  name: string;
  studentNo: string;
  balance: number | null;
}

/** 课次详情（SC-39）。 */
export interface LessonSessionDetail extends LessonSessionListItem {
  skuName: string;
  teacherId: string;
  teacherName: string;
  studentCount: number;
  schedule: {
    scheduleId: string;
    dayOfWeek: number;
    startTime: string;
    durationMinutes: number;
  } | null;
  students: SessionStudentSummary[];
}

/** 发布排课（硬冲突时 Transport 抛 ApiError，details 见 error.details.conflicts）。 */
export async function publishSchedules(dto: {
  classId: string;
  slots: ScheduleSlotInput[];
  effectiveFrom: string;
  effectiveTo: string;
  teacherId?: string;
  classroomId?: string;
}): Promise<PublishSchedulesResult> {
  return http.post<PublishSchedulesResult>(ApiPaths.scheduling.publish, dto);
}

/** 班级排课规则列表（班级详情排课本签）。 */
export async function listSchedules(classId: string): Promise<ScheduleListItem[]> {
  const res = await http.get<{ items: ScheduleListItem[] }>(
    ApiPaths.scheduling.listSchedules,
    { classId },
  );
  return res.items;
}

/** 结束排课（二次确认标记；取消未来课次）。 */
export async function endSchedule(id: string, confirm: boolean): Promise<EndScheduleResult> {
  return http.delete<EndScheduleResult>(ApiPaths.scheduling.end(id), { confirm });
}

/** 发布通知对象预览（5-5 timetable-projections：在班学生数 + 已关注监护人数去重）。 */
export interface NotificationPreview {
  activeStudentCount: number;
  followedGuardianCount: number;
}

/** 发布通知对象预览（ADM-19 发布对话框摘要；零关注返回 0 不报错）。 */
export async function getNotificationPreview(classId: string): Promise<NotificationPreview> {
  return http.get<NotificationPreview>(ApiPaths.scheduling.notificationPreview, {
    classId,
  });
}

/** 周历课次列表（日期区间，含首尾日；studentId 过滤时含 ENDED 历史班级课次；
 *  excludeCancelled 供排课工作台周历隐藏已取消课次，缺省原样返回；
 *  campusId 为全局校区范围口径（O-21 顶栏切换器决定，「全部校区」时不传）。 */
export async function listLessonSessions(
  from: string,
  to: string,
  studentId?: string,
  options?: { excludeCancelled?: boolean; campusId?: string },
): Promise<LessonSessionListItem[]> {
  const res = await http.get<{ items: LessonSessionListItem[] }>(
    ApiPaths.scheduling.lessonSessions,
    {
      from,
      to,
      ...(studentId ? { studentId } : {}),
      ...(options?.excludeCancelled ? { excludeCancelled: "true" } : {}),
      ...(options?.campusId ? { campusId: options.campusId } : {}),
    },
  );
  return res.items;
}

/** 课次详情（派生字段 + 学生摘要余额）。 */
export async function getLessonSessionDetail(id: string): Promise<LessonSessionDetail> {
  return http.get<LessonSessionDetail>(ApiPaths.scheduling.lessonSessionDetail(id));
}

/** 课次实例级调整（D-09：教室/上课日期/开始时间，至少一项）。 */
export async function adjustLessonSession(
  id: string,
  dto: { classroomId?: string; sessionDate?: string; startTime?: string },
): Promise<LessonSessionDetail> {
  return http.post<LessonSessionDetail>(ApiPaths.scheduling.adjustLessonSession(id), dto);
}

/** 取消单课次结果（class-scheduling:SC-55）。 */
export interface CancelLessonSessionResult {
  id: string;
  status: LessonSessionStatus;
}

/** 取消单课次（二次确认标记；仅待上课且未消课课次，终态不可恢复）。 */
export async function cancelLessonSession(
  id: string,
  confirm: boolean,
): Promise<CancelLessonSessionResult> {
  return http.post<CancelLessonSessionResult>(ApiPaths.scheduling.cancelLessonSession(id), {
    confirm,
  });
}
